import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLocationStore } from '@/store/location';

// Types
export interface Ingredient {
  name: string;
  quantity: number;
  unit: string;
  cost_per_unit?: number;
  notes?: string;
  is_optional?: boolean;
}

export interface Instruction {
  step_number: number;
  description: string;
  duration_minutes?: number;
  notes?: string;
}

export interface StandardizedTemplate {
  id: string;
  organization_id: string;
  location_id?: string;
  name: string;
  description?: string;
  category?: string;
  image_url?: string;
  type: 'recipe' | 'product' | 'bundle';
  ingredients: Ingredient[];
  instructions: Instruction[];
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  total_time_minutes?: number;
  servings: number;
  serving_size?: number;
  serving_unit?: string;
  estimated_cost_per_serving?: number;
  dietary_tags: string[];
  cuisine_type?: string;
  difficulty_level?: 'easy' | 'medium' | 'hard';
  source: 'ai_generated' | 'imported' | 'manual';
  source_url?: string;
  status: 'draft' | 'approved' | 'archived';
  version: number;
  parent_template_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplateInput {
  organization_id: string;
  location_id?: string;
  name: string;
  description?: string;
  category?: string;
  image_url?: string;
  type?: 'recipe' | 'product' | 'bundle';
  ingredients?: Ingredient[];
  instructions?: Instruction[];
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  servings?: number;
  serving_size?: number;
  serving_unit?: string;
  estimated_cost_per_serving?: number;
  dietary_tags?: string[];
  cuisine_type?: string;
  difficulty_level?: 'easy' | 'medium' | 'hard';
  source?: 'ai_generated' | 'imported' | 'manual';
  source_url?: string;
  status?: 'draft' | 'approved' | 'archived';
}

export interface UpdateTemplateInput extends Partial<CreateTemplateInput> {
  id: string;
}

export interface RecipeSearchParams {
  query: string;
  cuisineType?: string;
  dietaryRestrictions?: string[];
  maxResults?: number;
}

export interface RecipeGenerateParams {
  name: string;
  description?: string;
  cuisineType?: string;
  dietaryRestrictions?: string[];
  servings?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
}

export interface RecipeImportParams {
  url: string;
}

export interface AIRecipeResult {
  name: string;
  description: string;
  category?: string;
  cuisineType?: string;
  ingredients: Ingredient[];
  instructions: Instruction[];
  prepTimeMinutes?: number;
  cookTimeMinutes?: number;
  servings: number;
  servingUnit?: string;
  dietaryTags?: string[];
  difficultyLevel?: string;
}

// Hooks
export function useStandardizedTemplates(options?: { locationId?: string | null }) {
  const { profile } = useAuth();
  const { currentLocation } = useLocationStore();
  const organizationId = profile?.organization_id;
  
  // Use passed locationId or fall back to store's currentLocation
  // null means "all locations", undefined means "use store"
  const effectiveLocationId = options?.locationId !== undefined 
    ? options.locationId 
    : currentLocation?.id;

  return useQuery({
    queryKey: ['standardized-templates', organizationId, effectiveLocationId],
    queryFn: async () => {
      let query = supabase
        .from('standardized_templates')
        .select('*')
        .eq('organization_id', organizationId!)
        .order('updated_at', { ascending: false });

      // Filter by location only if a specific location is selected
      // When effectiveLocationId is null/undefined, show ALL templates (All Locations mode)
      if (effectiveLocationId) {
        query = query.or(`location_id.eq.${effectiveLocationId},location_id.is.null`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as StandardizedTemplate[];
    },
    enabled: !!organizationId,
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['standardized-templates', 'detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('standardized_templates')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as StandardizedTemplate;
    },
    enabled: !!id && id !== 'new',
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (input: Omit<CreateTemplateInput, 'organization_id'>) => {
      const { data, error } = await supabase
        .from('standardized_templates')
        .insert({
          ...input,
          organization_id: profile!.organization_id,
          ingredients: input.ingredients || [],
          instructions: input.instructions || [],
          dietary_tags: input.dietary_tags || [],
          created_by: profile!.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as StandardizedTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standardized-templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateTemplateInput) => {
      const { data, error } = await supabase
        .from('standardized_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as StandardizedTemplate;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['standardized-templates'] });
      queryClient.invalidateQueries({ queryKey: ['standardized-templates', 'detail', data.id] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('standardized_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standardized-templates'] });
    },
  });
}

export function useDuplicateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, newName }: { templateId: string; newName?: string }) => {
      const { data, error } = await supabase.rpc('duplicate_template', {
        source_template_id: templateId,
        new_name: newName || null,
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standardized-templates'] });
    },
  });
}

// Approve a template
export function useApproveTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      const { data, error } = await supabase
        .from('standardized_templates')
        .update({ status: 'approved', updated_at: new Date().toISOString() })
        .eq('id', templateId)
        .select()
        .single();

      if (error) throw error;
      return data as StandardizedTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standardized-templates'] });
      queryClient.invalidateQueries({ queryKey: ['template-stats'] });
    },
  });
}

// Update template image
export function useUpdateTemplateImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, imageUrl }: { templateId: string; imageUrl: string }) => {
      const { data, error } = await supabase
        .from('standardized_templates')
        .update({ image_url: imageUrl, updated_at: new Date().toISOString() })
        .eq('id', templateId)
        .select()
        .single();

      if (error) throw error;
      return data as StandardizedTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standardized-templates'] });
    },
  });
}

// AI Recipe Functions
export function useSearchRecipes() {
  return useMutation({
    mutationFn: async (params: RecipeSearchParams) => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/standardize-recipe`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            mode: 'search',
            ...params,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to search recipes');
      }

      const result = await response.json();
      return result.recipes as AIRecipeResult[];
    },
  });
}

export function useGenerateRecipe() {
  return useMutation({
    mutationFn: async (params: RecipeGenerateParams) => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/standardize-recipe`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            mode: 'generate',
            ...params,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to generate recipe');
      }

      const result = await response.json();
      return result.recipes[0] as AIRecipeResult;
    },
  });
}

export function useImportRecipe() {
  return useMutation({
    mutationFn: async (params: RecipeImportParams) => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/standardize-recipe`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            mode: 'import',
            ...params,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to import recipe');
      }

      const result = await response.json();
      return result.recipes[0] as AIRecipeResult;
    },
  });
}

// Template Stats Hook
export function useTemplateStats(options?: { locationId?: string | null }) {
  const { profile } = useAuth();
  const { currentLocation } = useLocationStore();
  const organizationId = profile?.organization_id;
  
  // Use passed locationId or fall back to store's currentLocation
  // null means "all locations", undefined means "use store"
  const effectiveLocationId = options?.locationId !== undefined 
    ? options.locationId 
    : currentLocation?.id;

  return useQuery({
    queryKey: ['standardized-templates', 'stats', organizationId, effectiveLocationId],
    queryFn: async () => {
      let query = supabase
        .from('standardized_templates')
        .select('type, status, category, location_id')
        .eq('organization_id', organizationId!);
      
      // Filter by location only if a specific location is selected
      if (effectiveLocationId) {
        query = query.or(`location_id.eq.${effectiveLocationId},location_id.is.null`);
      }

      const { data, error } = await query;

      if (error) throw error;

      const templates = data || [];
      return {
        total: templates.length,
        byType: {
          recipe: templates.filter(t => t.type === 'recipe').length,
          product: templates.filter(t => t.type === 'product').length,
          bundle: templates.filter(t => t.type === 'bundle').length,
        },
        byStatus: {
          draft: templates.filter(t => t.status === 'draft').length,
          approved: templates.filter(t => t.status === 'approved').length,
          archived: templates.filter(t => t.status === 'archived').length,
        },
        categories: [...new Set(templates.map(t => t.category).filter(Boolean))] as string[],
      };
    },
    enabled: !!organizationId,
  });
}

// Helper function to convert AI result to template input
export function aiResultToTemplateInput(
  result: AIRecipeResult,
  source: 'ai_generated' | 'imported' = 'ai_generated',
  sourceUrl?: string,
  locationId?: string
): Omit<CreateTemplateInput, 'organization_id'> {
  return {
    location_id: locationId,
    name: result.name,
    description: result.description,
    category: result.category,
    type: 'recipe',
    ingredients: result.ingredients,
    instructions: result.instructions,
    prep_time_minutes: result.prepTimeMinutes,
    cook_time_minutes: result.cookTimeMinutes,
    servings: result.servings || 4,
    serving_unit: result.servingUnit,
    dietary_tags: result.dietaryTags || [],
    cuisine_type: result.cuisineType,
    difficulty_level: result.difficultyLevel as 'easy' | 'medium' | 'hard' | undefined,
    source,
    source_url: sourceUrl,
    status: 'draft',
  };
}

